import { useState, useRef, useEffect } from 'react';
import { useTranslate } from '@/hooks/useTranslate';
import { ButtonType } from '@/types/components';
import { Button } from '@/components/Common/Button';
import X from '@/assets/icons/common/X.svg';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';

interface OptOutFormProps {
  isPending: boolean;
  onOptOut: () => void;
  onClose: () => void;
}

const PRIVACY_EMAIL = 'privacy@appcharge.com';
const PRIVACY_POLICY_URL = 'https://appcharge.com/payments-privacy-policy/';
const COPIED_FEEDBACK_MS = 1000;

export const OptOutForm = ({ isPending, onOptOut, onClose }: OptOutFormProps) => {
  const { t } = useTranslate();

  return (
    <div className=" flex w-full flex-col gap-4 text-base leading-6 text-base-foreground landscape:h-screen landscape:overflow-y-auto ">
      <div className="flex w-full items-start justify-between gap-3">
        <h2 id="do-not-sell-modal-title" className="flex-1 text-xl font-semibold leading-7">
          {t('doNotSellTitle')}
        </h2>
        <img
          data-testid="do-not-sell-modal-close"
          src={X}
          alt="x"
          className="mt-0.5 size-4 shrink-0 cursor-pointer opacity-70"
          onClick={onClose}
        />
      </div>

      <p>{t('doNotSellBody')}</p>

      <p>
        {t('doNotSellContactPrefix')} <span className="font-semibold">{PRIVACY_EMAIL}</span>
        <CopyEmailButton />
        {t('doNotSellContactSuffix')}
      </p>

      <p>
        {t('doNotSellPrivacyPrefix')}{' '}
        <a
          data-testid="do-not-sell-privacy-policy"
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold underline underline-offset-2"
        >
          {t('privacyPolicy')}
        </a>
      </p>

      <Button type={ButtonType.DARK} className="mt-3 h-11 text-base" isLoading={isPending} onClick={onOptOut}>
        {t('doNotSellOptOut')}
      </Button>
    </div>
  );
};

const CopyEmailButton = () => {
  const { t } = useTranslate();
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = async () => {
    navigator.clipboard.writeText(PRIVACY_EMAIL);
    setIsCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <button
      type="button"
      data-testid="do-not-sell-copy-email"
      aria-label={t('doNotSellCopyEmail')}
      className="ml-1 cursor-pointer bg-transparent p-0 text-sm text-muted-foreground"
      onClick={copy}
    >
      <FontAwesomeIcon icon={isCopied ? 'fa-regular fa-check' : 'fa-regular fa-copy'} />
    </button>
  );
};
